import { getAuthItems } from "./AuthItems";
import { createCodeChallenge, createCodeVerifier } from "./AuthValueCreator";
import { ErrorResponseData } from "../common/api/Api";
import * as rsiAuthApi from "./api/RsiAuthApi";
import { getAppEnv } from "../common/settings";
import { AxiosError } from "axios";

const SCOPE = [
  "offline_access",
  "openid",
  "uiframework:header:read",
  "aut:me:read",
  "aut:tenant:read",
].join(" ");

const authItems = getAuthItems();
const appEnv = getAppEnv();

/**
 * RSIの共通ヘッダを初期化します。
 */
export const initializeRsi = () => {
  window.rsi.init({
    getToken: (): string => {
      return authItems.accessToken;
    },
    updateToken: (): Promise<string> => {
      return getTokenAndUpdateItemsByRefeshToken();
    },
    logoutAfterUri: (): Promise<string> => {
      return createAuthorizeURL(false);
    },
  });
};

/**
 * アクセストークンが期限切れかどうかを判断します。
 *
 * @returns アクセストークンが期限切れの場合はtrue、そうでない場合はfalse
 */
export const isExpiredAccessToken = (): boolean => {
  return authItems.isExpiredAccessToken();
};

/**
 * 認証用のURLを作成します。
 *
 * @param isLogin - ログインかそれ以外を表すフラグ
 * @returns 認証用のURL
 */
export const createAuthorizeURL = async (isLogin: boolean = true): Promise<string> => {
  const codeVerifier = createCodeVerifier();
  authItems.codeVerifier = codeVerifier;

  const codeChallenge = await createCodeChallenge(codeVerifier);

  const queryObj: Record<string, string> = {
    client_id: appEnv.clientId,
    redirect_uri: appEnv.baseUrl + "/app/login",
    response_type: "code",
    scope: SCOPE,
    code_challenge: codeChallenge,
    code_challenge_method: "S256",
  };
  if (isLogin) {
    // ログアウト時はトップ画面に戻すため、stateはログインのみ
    queryObj["state"] = window.location.pathname + window.location.search;
  } else {
    queryObj["state"] = "/app";
  }

  const url = new URL(appEnv.accountsUrl + "/v1/aut/oauth/provider/authorize");
  const params = new URLSearchParams(queryObj);
  url.search = params.toString();
  return url.href;
};

/**
 * トークンを取得し、認証用アイテムを更新します。
 *
 * @param code - 認証画面で作成されたコード
 * @returns 処理結果
 */
export const getTokenAndUpdateItems = async (code: string): Promise<void | AxiosError> => {
  const result = await rsiAuthApi.getToken(code, authItems.codeVerifier);
  if (result.error) {
    return result.error;
  }

  authItems.accessToken = result.accessToken;
  authItems.refleshToken = result.refreshToken;
  authItems.loginTime = Date.now().toString();
};

const getTokenAndUpdateItemsByRefeshToken = async (): Promise<string> => {
  const result = await rsiAuthApi.getTokenByRefeshToken(authItems.refleshToken);
  if (result.error) {
    return "";
  }

  authItems.accessToken = result.accessToken;
  authItems.loginTime = Date.now().toString();
  return result.accessToken;
};

/**
 * レスポンスがトークンが期限切れのかどうかを判定します。
 *
 * @param data - レスポンスデータ
 * @returns 期限切れの場合はtrue、それ以外の場合はfalse
 */
export const isExpiredTokenResponse = (data: ErrorResponseData | undefined): boolean => {
  return (
    data?.status === 401 &&
    data.exception === "org.springframework.web.server.ResponseStatusException" &&
    data.message === "USER_UNAUTHORIZED"
  );
};

/**
 * レスポンスがライセンスがないエラーかどうかを判定します。
 *
 * @param data - レスポンスデータ
 * @returns 期限切れの場合はtrue、それ以外の場合はfalse
 */
export const isNoLicenseResponse = (data: ErrorResponseData | undefined): boolean => {
  return (
    data?.status === 401 &&
    data.exception === "org.springframework.web.server.ResponseStatusException" &&
    data.message === "HAS_NO_LICENSE"
  );
};
